/**
 * Formata a taxa a.m. sem afirmar juros zero onde a taxa ainda NÃO existe.
 *
 * Cartão consignado e cartão benefício nascem com `taxaAm = 0`: quando o
 * servidor pede o cartão, ele informa só o limite pretendido — a taxa é do
 * rotativo e quem define é o banco, na análise. Só que a tela renderizava
 * `(0 * 100).toFixed(2)` e exibia **0.00%**, que qualquer pessoa lê como
 * "sem juros". Numa proposta "Em análise pelo banco" isso é uma promessa que
 * o produto não faz. Cliente 13/08/2026.
 *
 * Assim que o banco aprova (ou edita a proposta antes de aprovar), `taxaAm`
 * passa a ser > 0 e volta a aparecer o percentual normalmente — este texto
 * some sozinho, sem depender de mais nada.
 *
 * Fora dos cartões, zero continua sendo exibido como 0,00%: no plano de
 * telemedicina, por exemplo, é verdade — é mensalidade, não empréstimo.
 *
 * TERCEIRO CASO, 27/08/2026: contrato **legado** — a dívida que já estava na
 * folha quando a Atlas chegou. Ali a taxa não é zero nem "a definir": ela é
 * **desconhecida**, e vai continuar sendo, porque a Atlas não tem a CCB e nunca
 * vai ter. O contrato foi feito no balcão da consignatária. `—` é a única coisa
 * honesta a exibir, e é diferente das outras duas: "A definir pelo banco"
 * prometeria que um dia aparece.
 */
export function fmtTaxaAm(
  taxaAm: number,
  contrato?: {
    tipoContrato?: string | null;
    tipoMargem?: string | null;
    /** `false` = contrato legado: a Atlas não tem a CCB. */
    dadosFinanceirosConhecidos?: boolean;
  },
): string {
  // Vem antes do teste de `> 0` de propósito: se algum dia um legado chegar com
  // taxa preenchida por outra via, ela ainda não é uma taxa que a Atlas conferiu.
  if (contrato?.dadosFinanceirosConhecidos === false) return "—";
  if (taxaAm > 0) return `${(taxaAm * 100).toFixed(2)}%`;
  const ehCartao =
    contrato?.tipoContrato === "ECONSIGNADO"
    || (contrato?.tipoMargem ?? "").startsWith("CARTAO");
  return ehCartao ? "A definir pela instituição" : `${(taxaAm * 100).toFixed(2)}%`;
}

/**
 * Valor financiado, sem afirmar "R$ 0,00" onde o valor é DESCONHECIDO.
 *
 * Mesmo raciocínio do `fmtTaxaAm`, outro campo: no contrato legado a Atlas não
 * tem a CCB, então não sabe quanto foi financiado — o número chega zero, e
 * "R$ 0,00" afirmaria um empréstimo de valor nenhum. A parcela, essa sim, é
 * conhecida: vem da folha.
 */
export function fmtValorContrato(
  valor: number,
  contrato: { dadosFinanceirosConhecidos?: boolean } | undefined,
  formatar: (n: number) => string,
): string {
  if (contrato?.dadosFinanceirosConhecidos === false) return "—";
  return formatar(valor);
}
